/*
 * My Trades (draft 9, FEATURE-MAP 8): the clients' own hub. One sign-in for every brand a client uses (their phone and a
 * texted 6-digit code). Clients are not logins: the hub has its own codes and sessions.
 */
import { z } from "zod";
import { addressParts } from "./address.js";

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const portalCodeBody = z
  .object({
    /** Left out when a hub opened from an invoice or quote link asks for its code: it goes to that client's own phone. */
    phone: z.string().trim().min(10).max(20).optional(),
    /** The brand's slug the client is signing in from (its own site), so the code comes from that brand's line. */
    brand: z
      .string()
      .trim()
      .regex(/^[a-z0-9-]{1,80}$/)
      .optional(),
    /** The web address the hub is open at. With no brand, the brand at that address sends the code. */
    origin: z.string().trim().max(200).optional(),
  })
  .strict();
export const portalVerifyBody = z
  .object({ phone: z.string().trim().min(10).max(20).optional(), code: z.string().regex(/^\d{6}$/, "Type the 6 numbers.") })
  .strict();
export const portalVerifyAnswer = z.object({ token: z.string() });

const visitTime = {
  /** The visit's ref ("<job id>.<date>" until it is a row), for opening it in the hub. */
  ref: z.string(),
  date: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  /** The arrival window the job promises (Arriving between 1:00 PM and 2:00 PM), when it has one. */
  arriveFrom: z.string().nullable(),
  arriveTo: z.string().nullable(),
  job: z.string(),
  crew: z.array(z.string()),
  address: z.string().nullable(),
};

export const portalBrand = z.object({
  companyId: z.uuid(),
  name: z.string(),
  /** cleaning, lawn, washers or agency: picks the words and the quick picks on Request a quote. */
  trade: z.string(),
  /** The brand kit (Settings, Brand): top band color, its darker shade for buttons, the footer color, the white logo. */
  color: z.string().nullable(),
  deep: z.string(),
  foot: z.string(),
  logoUrl: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** The brand's own web address: its pay and quote links live there, and the hub opens on it there. */
  url: z.string(),
  firstName: z.string(),
  /** The address on file, shown on Request a quote. */
  address: z.string().nullable(),
  /** Cards can be saved here (the company takes cards online). */
  cardsOn: z.boolean(),
  /** The card on file (brand and last 4 only) and whether automatic charges are on with it. */
  card: z.object({ brand: z.string(), last4: z.string(), auto: z.boolean() }).nullable(),
  next: z.array(
    z.object({
      ...visitTime,
      /** The price list service the visit was priced from, for add-ons offered with that service only. */
      serviceId: z.string().nullable(),
      /** Add-ons can still be added or taken off: a day after today, not started, not billed. */
      addonsOpen: z.boolean(),
      /** A visit of a repeating plan: add-ons with a plan price cost that here. */
      onPlan: z.boolean().default(false),
      /** Add-ons the client put on this visit here: the add-on's id, how many, and its price in all. */
      addons: z.array(z.object({ id: z.string(), count: z.number(), cents: z.number() })),
    }),
  ),
  past: z.array(z.object({ ...visitTime, report: z.string().nullable() })),
  invoices: z.array(
    z.object({
      number: z.number().nullable(),
      title: z.string(),
      issuedOn: z.string().nullable(),
      dueOn: z.string().nullable(),
      paidOn: z.string().nullable(),
      totalCents: z.number(),
      balanceCents: z.number(),
      state: z.enum(["open", "past_due", "paid"]),
      payLink: z.string(),
    }),
  ),
  quotes: z.array(
    z.object({
      number: z.number(),
      title: z.string().nullable(),
      status: z.string(),
      sentOn: z.string().nullable(),
      totalCents: z.number(),
      link: z.string(),
      /**
       * An open plan offer (sent, repeating, not booked): kept under Offers so they can come back to it (owner,
       * 2026-11-02). often: "Every 2 weeks"; perVisitCents: each visit after the first.
       */
      offer: z.object({ often: z.string(), perVisitCents: z.number() }).nullable().default(null),
    }),
  ),
  /** What they asked for: from the website, a call, or Get a quote here. Tapping one opens what they sent and its quote. */
  requests: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      askedOn: z.string(),
      state: z.enum(["new", "sent", "converted", "lost"]),
      /** All they wrote, when it came from Get a quote here. */
      what: z.string().nullable(),
      wantsOn: z.string().nullable(),
      address: z.string().nullable(),
      photos: z.number(),
      /** The quote made for it, once it was sent: its page, to see and say yes. */
      quote: z.object({ number: z.number(), link: z.string() }).nullable(),
    }),
  ),
  /** Get a quote prices on the quoter (a cleaning brand with a price list); else it is a request the office prices. */
  quoter: z.boolean(),
  /** Keep it this clean: plan prices after a standard, deep or move in clean, with no schedule yet (hub-booking.ts). */
  planOffer: z.object({ prices: z.array(z.object({ id: z.string(), label: z.string(), perVisitCents: z.number() })) }).nullable(),
  /** The price list's add-ons a client may put on an upcoming visit. only: offered with that service only. */
  addons: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      cents: z.number(),
      /** The flat price on a plan visit, when it has one. */
      planCents: z.number().nullable().default(null),
      each: z.boolean(),
      only: z.string().nullable(),
      about: z.string().nullable(),
    }),
  ),
  /** Repeating plans, for Add a visit at the plan price. */
  plans: z.array(z.object({ jobId: z.uuid(), job: z.string(), often: z.string(), priceCents: z.number() })),
  /** Refer a friend: their link and what it is worth to each, when the brand has a website. Homes only. */
  refer: z.object({ link: z.string(), amountCents: z.number() }).nullable(),
  /** A marketing client of the agency: their results this month so far (owner answer 119). Null for every other brand. */
  results: z
    .object({
      health: z.enum(["good", "watch", "bad", "none"]),
      leadsNow: z.number().nullable(),
      leadsBefore: z.number().nullable(),
      costPerLeadCents: z.number().nullable(),
      reviews: z.number().nullable(),
      rating: z.number().nullable(),
      rankNow: z.number().nullable(),
      rankBefore: z.number().nullable(),
      planCents: z.number(),
    })
    .nullable(),
});
export type PortalBrand = z.infer<typeof portalBrand>;

/** A sister brand the client does not use yet, offered on the hub (cross-sell). */
export const portalOther = z.object({
  brandId: z.uuid(),
  name: z.string(),
  trade: z.string(),
  color: z.string().nullable(),
  logoUrl: z.string().nullable(),
  website: z.string().nullable(),
  /** The brand has a ZIP list and we have no ZIP for the client: Request a quote asks for their address with its ZIP. */
  needsZip: z.boolean(),
});
export type PortalOther = z.infer<typeof portalOther>;

export const portalAnswer = z.object({
  brands: z.array(portalBrand),
  others: z.array(portalOther),
  /** A change asks for a code first: opened from a link, or no code in the last hour. */
  byLink: z.boolean().optional(),
  /** The last 4 of the phone the code goes to, when a change needs a code. */
  phoneEnd: z.string().optional(),
});
/** Open the hub from a pay link (/i/), quote link (/q/) or the My Trades link in our emails (/my?c=). */
export const portalLinkBody = z
  .object({ kind: z.enum(["invoice", "quote", "client"]), link: z.string().trim().max(64) })
  .strict();
export type PortalAnswer = z.infer<typeof portalAnswer>;

/** Add a visit: a plan client books one more visit at their plan price on an open day (not today). */
export const portalAddVisitBody = z.object({ companyId: z.uuid(), jobId: z.uuid(), date: day }).strict();
export const portalOpenDays = z.object({ days: z.array(z.string()) });

/**
 * Add-ons on an upcoming visit: the add-on ids and how many (1 for a yes or no add-on). It replaces the ones the client put
 * on that visit before; an add-on left out comes off.
 */
export const portalVisitAddonsBody = z
  .object({
    companyId: z.uuid(),
    ref: z.string().trim().min(1).max(80),
    addons: z.record(z.string().trim().min(1).max(20), z.number().int().min(1).max(20)),
  })
  .strict()
  .refine((b) => Object.keys(b.addons).length <= 30, "Too many add-ons.");
export const portalVisitAddonsAnswer = z.object({ ok: z.literal(true), totalCents: z.number() });

/**
 * Request a quote: from a brand they use (companyId) or a sister brand they do not use yet (brandId). The office gets it
 * as a request, like one from the website.
 */
export const portalRequestBody = z
  .object({
    companyId: z.uuid().optional(),
    brandId: z.uuid().optional(),
    what: z.string().trim().min(3, "Tell us what you need.").max(600),
    day: day.optional(),
    /** Another address than the one on file, in parts (owner rule: addresses are never one line). */
    place: addressParts.optional(),
    /** The old one line address, from a hub page opened before the parts came in. */
    address: z.string().trim().max(160).optional(),
  })
  .strict()
  .refine((b) => Boolean(b.companyId) !== Boolean(b.brandId), "Pick one business.");
/** id and companyId: where the request landed, for its photos (portalRequestPhotoBody). */
export const portalRequestAnswer = z.object({ ok: z.literal(true), business: z.string(), id: z.uuid(), companyId: z.uuid() });

/** The most photos one request takes. */
export const REQUEST_PHOTOS_MAX = 5;
/**
 * One photo for a request just sent from Get a quote, one call each so every call stays under the public size limit
 * (the hub makes each photo smaller first). Only the client who sent the request, only within a day.
 */
export const portalRequestPhotoBody = z
  .object({
    companyId: z.uuid(),
    requestId: z.uuid(),
    name: z.string().trim().min(1).max(200),
    mime: z.enum(["image/jpeg", "image/png", "image/webp"]),
    dataBase64: z.string().min(1).max(1_000_000, "That photo is too big."),
  })
  .strict();

/** Wallet: save a card on our own page; it replaces the one on file. There is no Remove. */
export const portalCardBody = z.object({ companyId: z.uuid() }).strict();
export const portalCardDoneBody = z.object({ companyId: z.uuid(), intentId: z.string().min(5).max(200) }).strict();
